import { create } from 'zustand';
import type { AccountingStatusFilter } from '../../app/invoices/components/invoice-status-cards';

interface InvoicesListState {
  // Pagination
  page: number;
  limit: number;

  // Selection
  selectedIds: string[];
  selectedLoadId: string | null;

  // Filter
  statusFilter: AccountingStatusFilter;

  // Table filters and columns
  columnFilters: Record<string, string>;
  visibleColumns: string[];

  // Sorting
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface InvoicesListActions {
  // Pagination actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;

  // Selection actions
  setSelectedIds: (ids: string[]) => void;
  setSelectedLoadId: (id: string | null) => void;

  // Filter actions
  setStatusFilter: (status: AccountingStatusFilter) => void;

  // Table filters and columns actions
  setColumnFilter: (column: string, value: string) => void;
  setColumnFilters: (filters: Record<string, string>) => void;
  clearColumnFilters: () => void;
  setVisibleColumns: (columns: string[]) => void;
  toggleColumn: (column: string) => void;

  // Sorting actions
  setSortBy: (sortBy: string) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  toggleSortOrder: () => void;

  // Utility actions
  resetFilters: () => void;
  resetAll: () => void;
}

type InvoicesListStore = InvoicesListState & InvoicesListActions;

// Default visible columns for the invoices table
const DEFAULT_VISIBLE_COLUMNS = [
  'loadNumber',
  'status',
  'accountingStatus',
  'pickupDate',
  'originLocation',
  'destinationLocation',
  'deliveryDate',
  'customerName',
  'driverName',
  'truckNumber',
  'totalRevenue',
];

const initialState: InvoicesListState = {
  page: 1,
  limit: 25,
  selectedIds: [],
  selectedLoadId: null,
  statusFilter: 'all',
  columnFilters: {},
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  sortBy: 'deliveryDate',
  sortOrder: 'desc',
};

export const useInvoicesListStore = create<InvoicesListStore>()((set) => ({
  ...initialState,

  // Pagination actions
  setPage: (page) => set({ page }),
  setLimit: (limit) => set({ limit, page: 1 }),

  // Selection actions
  setSelectedIds: (selectedIds) => set({ selectedIds }),
  setSelectedLoadId: (selectedLoadId) => set({ selectedLoadId }),

  // Filter actions
  setStatusFilter: (statusFilter) => set({ statusFilter, page: 1, selectedIds: [] }),

  // Table filters and columns actions
  setColumnFilter: (column, value) =>
    set((state) => ({
      columnFilters: { ...state.columnFilters, [column]: value },
    })),
  setColumnFilters: (columnFilters) => set({ columnFilters }),
  clearColumnFilters: () => set({ columnFilters: {} }),
  setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: state.visibleColumns.includes(column)
        ? state.visibleColumns.filter((c) => c !== column)
        : [...state.visibleColumns, column],
    })),

  // Sorting actions
  setSortBy: (sortBy) => set({ sortBy }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  toggleSortOrder: () =>
    set((state) => ({
      sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc',
    })),

  // Utility actions
  resetFilters: () =>
    set({
      statusFilter: 'all',
      columnFilters: {},
      page: 1,
      selectedIds: [],
    }),
  resetAll: () => set(initialState),
}));
